<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2" v-model="selectedValue">
        <el-option key="quickView" :label="$t('views.common.listView.quickView')" value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
        </el-option>
        <el-option
          key="export"
          :label="$t('views.common.listView.selectedRows.export')"
          value="export"
        >
        </el-option>
      </el-select>

      <el-input
        :placeholder="$t('views.common.listView.searchRows')"
        class="w-full col-span-12 md:col-span-7"
        :prefix-icon="Search"
        v-model="searchTerm"
      />
      <div class="col-span-12 md:col-span-3 flex items-center gap-4">
        <ToggleGroup v-model="outOfStock" class="gap-2 flex-wrap lg:flex-nowrap" type="multiple">
          <ToggleGroupItem value="true" class="data-[state=on]:text-red-600 bg-transparent p-1">
            <Icon icon="material-symbols:history" width="24" height="24" />
          </ToggleGroupItem>
        </ToggleGroup>
        <template v-if="$can('create', '/inventory/items')">
          <horizontal-button
            :title="$t('views.inventory.items.newSimpleItem')"
            @click.prevent="$router.push($Route.INVENTORY_ITEMS_NEW_SIMPLE_ITEM)"
          />
        </template>
      </div>
    </div>

    <ag-grid-vue
      v-if="rowModelType"
      class="ag-theme-balham mt-4 md:mt-0"
      :columnDefs="columnDefs"
      :rowData="rowData"
      rowSelection="multiple"
      :enable-rtl="$ctx.getDir() === 'rtl'"
      style="height: 85vh"
      :pagination="selectedValue == 'allItems'"
      :paginationAutoPageSize="selectedValue == 'allItems'"
      :defaultColDef="defaultColDef"
      @cell-double-clicked="onCellDoubleClicked"
      :onGridReady="onGridReady"
      :rowBuffer="rowBuffer"
      :rowModelType="rowModelType"
      :cacheBlockSize="cacheBlockSize"
      :cacheOverflowSize="cacheOverflowSize"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :infiniteInitialRowCount="infiniteInitialRowCount"
      :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community'
import Item, { ItemPaginated, itemI18nMessages } from '@/models/inventory/Item'
// import ItemTag from "@/models/inventory/Tag";
import HorizontalButton from '@/components/HorizontalButton.vue'

import { formatDinero, formatMoney } from '@/utils/money'
import { useI18n } from 'vue-i18n'
import { ProcessCellForExportParams } from 'ag-grid-community'
import { Search } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { onBeforeMount } from 'vue'
import { useSettingsStore } from '@/stores/settingsStore'
import { ElMessage } from 'element-plus'
import { ItemBatch, ItemBatchPaginated } from '@/models/inventory/ItemBatch'
import ToggleGroup from '@/components/ui/toggle-group/ToggleGroup.vue'
import ToggleGroupItem from '@/components/ui/toggle-group/ToggleGroupItem.vue'
import { Icon } from '@iconify/vue/dist/iconify.js'
import { formatPostgresDate } from '@/utils/date'
ModuleRegistry.registerModules([InfiniteRowModelModule])

export default defineComponent({
  name: 'items',

  components: {
    AgGridVue,
    HorizontalButton,
    ToggleGroup,
    ToggleGroupItem,
    Icon
  },

  setup() {
    const messages = {
      en: {
        ...itemI18nMessages.en,
        itemPriceUpdated: 'Item price has been updated',
        unlimited: 'Unlimited',
        quantity: 'Quantity',
        batchNumber: 'Batch Number',
        manufactureDate: 'Manufacturing Date',
        expirationDate: 'Expiration Date'
      },
      ar: {
        ...itemI18nMessages.ar,
        itemPriceUpdated: 'تم تحديث سعر المنتج بنجاح',
        unlimited: 'غير محدود',
        quantity: 'الكمية',
        batchNumber: 'رقم الوجبة',
        manufactureDate: 'تاريخ الصنع',
        expirationDate: 'تاريخ الإنتهاء'
      }
    }

    const { t } = useI18n({
      messages
    })

    return {
      t,
      Search
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: ItemBatch[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)
    const outOfStock = ref<Array<string>>([])
    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: '',
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,

      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      outOfStock
    }
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      cellStyle: { textAlign: 'center' }
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: this.t('name'),
        field: 'name',
        checkboxSelection: true,
        autoHeight: true,
        cellStyle: { textAlign: 'start' },
        headerCheckboxSelection: true,
        minWidth: 300,
        cellRenderer: (params: ICellRendererParams) => {
          const itemBatch = params.node?.data as ItemBatch
          if (!itemBatch) return ''
          return `<span>${itemBatch.name} <span style="color: #6b7280; font-weight: 800;">(${itemBatch.batchNumber})</span></span>`
        },
      },
      {
        headerName: this.t('listPriceAmount'),
        field: 'listPriceAmount',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ data }) => {
          const item = data as ItemBatch
          if (!item) return ''
          return item.listPriceAmount ? formatMoney(item.listPriceAmount, this.$ctx.currency) : ''
        },
        cellStyle: { textAlign: 'center' },
        minWidth: 200
      },
      {
        headerName: this.t('salePriceAmount'),
        field: 'salePriceAmount',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ data }) => {
          const item = data as ItemBatch
          if (!item) return ''
          return item.salePriceAmount ? formatMoney(item.salePriceAmount, this.$ctx.currency) : ''
        },
        cellStyle: { textAlign: 'center' },
        minWidth: 200
      },
      {
        headerName: this.t('batchNumber'),
        field: 'batchNumber',
        cellStyle: { textAlign: 'left' },
        minWidth: 150
      },
      {
        headerName: this.t('manufactureDate'),
        field: 'manufactureDate',
        cellStyle: { textAlign: 'left' },
        minWidth: 200,
        valueGetter: (params) => {
          const itemBatch = params.node?.data as ItemBatch
          if (!itemBatch) return ''
          if (itemBatch.manufactureDate) {
            const manufactureDate = new Date(itemBatch.manufactureDate)
            return formatPostgresDate(String(manufactureDate), 'DD/MM/YYYY')
          }
          return ''
        },
      },
      {
        headerName: this.t('expirationDate'),
        field: 'expirationDate',
        cellStyle: { textAlign: 'left' },
        minWidth: 200,
        cellRenderer: (params: ICellRendererParams) => {
          const itemBatch = params.node?.data as ItemBatch
          if (!itemBatch || !itemBatch.expirationDate) return ''
          
          const expirationDate = new Date(itemBatch.expirationDate)
          const currentDate = new Date()
          const oneMonthFromNow = new Date()
          oneMonthFromNow.setMonth(currentDate.getMonth() + 1)
          
          const formattedDate = formatPostgresDate(String(expirationDate), 'DD/MM/YYYY')
          
          // Check if expired (red)
          if (expirationDate < currentDate) {
            return `<span style="color: #ef4444; font-weight: 600;">${formattedDate}</span>`
          }
          // Check if within one month of expiration (yellow)
          else if (expirationDate <= oneMonthFromNow) {
            return `<span style="color: #eab308; font-weight: 600;">${formattedDate}</span>`
          }
          // Normal color
          else {
            return `<span>${formattedDate}</span>`
          }
        },
      },
      {
        headerName: this.t('quantity'),
        field: 'quantity',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        cellStyle: { textAlign: 'center' },
        minWidth: 200
      },
      {
        headerName: 'Barcode',
        field: 'barcode',
        cellStyle: { textAlign: 'left' },
        minWidth: 150
      },
      {
        headerName: this.t('vendor'),
        field: 'vendorName',
        minWidth: 150
      },
      {
        headerName: this.t('itemCategory'),
        field: 'itemCategoryName',
        minWidth: 150
      },
    ]
  },

  mounted() {},

  watch: {
    outOfStock() {
      this.updateFilter()
    },
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export' || oldValue == 'restore' || oldValue == 'deletePermanently') {
        return
      }

      if (
        this.selectedValue === 'export'
      ) {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'export') {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      } 
    }
  },

  methods: {
    updateFilter() {
      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },
    async updateRowData(softDeleted: boolean = false) {
      this.gridApi?.showLoadingOverlay()

      try {
        const urlParams = new URLSearchParams()
        if (this.outOfStock.length) {
          urlParams.set('outOfStock', this.outOfStock.join(','))
        }
        const data = await this.$http.get<ItemBatch[]>(Item.ENDPOINT+"/item-batches?"+urlParams.toString())
        if (softDeleted) {
          this.rowData = data
            .map((item) => item)
            .filter((item) => item.deletedAt != null)
        } else {
          this.rowData = data
            .map((item) => item)
            .filter((item) => item.deletedAt == null)
        }

        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          const urlParams = new URLSearchParams()
          if (this.outOfStock) {
            urlParams.set('outOfStock', this.outOfStock.join(','))
          }
          let url = `${Item.ENDPOINT}/item-batches?startRow=${params.startRow}&endRow=${params.endRow}&${urlParams.toString()}`
          if (params.sortModel.length) {
            url += `&colId=${params.sortModel[0].colId}&sort=${params.sortModel[0].sort}`
          }

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          const res = await this.$http.get<ItemBatchPaginated>(url)
          this.rowData = res.data.map((item) => item)

          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      this.gridApi?.sizeColumnsToFit()

      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.updateRowData()
      }
    },


    processCellForExport(params: ProcessCellForExportParams) {
      if (params.column.getColDef().headerName === 'List Price') {
        return params.node?.data.listPriceAmount
          ? formatMoney(params.node?.data.listPriceAmount, this.$ctx.currency)
          : ''
      } else if (params.column.getColDef().headerName === 'Sale Price') {
        return params.node?.data.salePriceAmount
          ? formatMoney(params.node?.data.salePriceAmount, this.$ctx.currency)
          : ''
      } else {
        //if the cell doesn't belong to any of the above don't change it
        return params.value
      }
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    onCellDoubleClicked(event: any) {
      let id = event.data.id;
      if (event.data.itemParentID) {
        id = event.data.itemParentID;
      }
      this.$router.push(this.$Route.INVENTORY_ITEMS_ITEM_VIEW.replace(':id', id))
    }
  }
})
</script>

<style></style>
